<template>
	<div v-if="paymentRequired" class="brand-agreement-surface brand-agreement-payment" @click="$emit('click', $event)" :style="paymentStyle" :id="item.id">
		<div v-if="showPaymentElement">
			<div id="payment-element"></div>
			<div class="text-left mt-3 row-format gap-1 align-center">
				<div class="ml-auto font-14 font-gray_80">Pay securely with</div>
				<img src="/images/invoices/stripe-logo.svg" alt="Stripe Logo" width="50" />
			</div>
		</div>
		<div v-else-if="verification" class="text-left font-14">
			<div class="font-16 brand-medium">{{ $t('agreement-payment.verify-title') }}</div>
			<div class="mt-1">
				{{ $t(verification.type === 'amounts' ? 'agreement-payment.verify-amounts' : 'agreement-payment.verify-descriptor-code') }}
			</div>
			<div v-if="verification.arrivalDate" class="mt-1 font-gray_80">
				{{ $t('agreement-payment.verify-arrival', { date: DateTime.fromJSDate(verification.arrivalDate).toLocaleString(DateTime.DATE_MED) }) }}
			</div>
			<div class="row-format gap-2 align-center mt-3">
				<template v-if="verification.type === 'amounts'">
					<v-text-field
						v-for="(amount, index) in verificationAmounts"
						:key="index"
						v-model="verificationAmounts[index]"
						:label="$t(index === 0 ? 'agreement-payment.first-amount' : 'agreement-payment.second-amount')"
						placeholder="0.32"
						inputmode="decimal"
						hide-details
						dense
						outlined
						persistent-placeholder
						@keyup.enter="verifyMicrodeposits"
					/>
				</template>
				<v-text-field
					v-else
					v-model="verificationCode"
					:label="$t('agreement-payment.descriptor-code')"
					placeholder="SM11AA"
					maxlength="6"
					hide-details
					dense
					outlined
					persistent-placeholder
					@keyup.enter="verifyMicrodeposits"
				/>
				<button type="button" class="agreement-finish row-format align-center gap-2" :disabled="verifying" @click="verifyMicrodeposits">
					<v-progress-circular v-if="verifying" indeterminate size="16" width="2"></v-progress-circular>
					<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{ $t('agreement-payment.verify') }}</span>
				</button>
			</div>
			<div v-if="verificationError" role="alert" class="mt-2 payment-verification__error">{{ verificationError }}</div>
			<div class="row-format gap-3 mt-2 align-center">
				<a v-if="verification.hostedUrl" :href="verification.hostedUrl" target="_blank" rel="noopener">
					{{ $t('agreement-payment.verify-on-stripe') }}
				</a>
				<a href="#" @click.prevent="checkVerification">{{ $t('agreement-payment.check-status') }}</a>
				<div class="ml-auto row-format gap-1 align-center">
					<div class="font-14 font-gray_80">Pay securely with</div>
					<img src="/images/invoices/stripe-logo.svg" alt="Stripe Logo" width="50" />
				</div>
			</div>
		</div>
		<div v-else class="row-format text-left align-center font-14">
			<div>
				<span style="text-transform: capitalize;">
					{{ $t(`agreement-payment.status-${statusKey}`) }} -
					{{
						$formatters.dollars(item.stripePaymentIntent.amount / 100, true, true, item.stripePaymentIntent.currency)
					}}<template v-if="paymentMethodLabel"> - {{ paymentMethodLabel }}</template>
				</span><br />
				<template v-if="item.stripePaymentIntent.timestamp">
					{{ DateTime.fromISO(item.stripePaymentIntent.timestamp).toLocaleString(DateTime.DATETIME_FULL) }}<br />
				</template>
				<div v-if="summaryBlockedReason" class="font-gray_80">{{ $t(`agreement-payment.blocked-${summaryBlockedReason}`) }}</div>
				<a v-if="agreement.invoiceId" :href="`/invoice/${agreement.invoiceId}`" target="_blank">{{ agreement.invoiceNumberFormatted }}</a>
			</div>
			<div class="ml-auto row-format gap-1 align-center">
				<div class="ml-auto font-14 font-gray_80">Pay securely with</div>
				<img src="/images/invoices/stripe-logo.svg" alt="Stripe Logo" width="50" />
			</div>
		</div>
	</div>
</template>

<script>
	import chroma from 'chroma-js';
	import axios from 'axios';
	import qs from 'qs';
	import { DateTime } from 'luxon';
	import { packageOfferPaymentAmount } from '@/modules/agreements/packageOfferFinalization';
	import {
		isPaymentAuthorized,
		microdepositVerification,
		parseMicrodepositAmount,
		paymentBlockedReason,
	} from '@/modules/agreements/schema/paymentIntentState';
	import CustomFontService from '@/modules/utils/CustomFontService';
	import ResponsiveMixin from "./ResponsiveMixin";
	import { agreementBranding, agreementStyles, formBrandStyle } from '@moxie/branding';

	export default {
		name: 'PaymentV2',

		props: ['agreement', 'item', 'tokens','isMobile','pageWidth'],

		components: {},

		mixins: [ResponsiveMixin],

		data: function() {
			return {
				DateTime: DateTime,
				showPaymentElement: true,
				stripe: null,
				elements: null,
				paymentElement: null,
				httpClient: null,
				selectedPaymentMethodType: 'card',
				syncedAmount: null,
				amountSync: Promise.resolve(),
				nextAction: null,
				verificationCode: '',
				verificationAmounts: ['', ''],
				verificationError: null,
				verifying: false,
			};
		},

		mounted() {
			this.httpClient = axios.create({
				baseURL: this.$store.getters.getPodUrl,
				withCredentials: true,
			});

			this.$store.state.agreementPaymentElement = this;
			this.$nextTick(() => this.setupStripePayment());

			// Clients often verify on Stripe's hosted page in another tab; pick that up when they return.
			window.addEventListener('focus', this.refreshPendingVerification);
			document.addEventListener('visibilitychange', this.refreshPendingVerification);
		},

		beforeDestroy() {
			if (this.paymentElement) this.paymentElement.unmount();
			window.removeEventListener('focus', this.refreshPendingVerification);
			document.removeEventListener('visibilitychange', this.refreshPendingVerification);
		},

		methods: {
			isPackageOfferAgreement() {
				return (this.agreement.pages || []).some((page) =>
					(page.schema || []).some((block) => block.type === 'PackageOffer')
				);
			},
			async setupStripePayment() {
				if (!this.paymentRequired || this.paymentElement || !document.getElementById('payment-element')) return;
				if (this.agreement.account.stripeAccountId) {
					this.stripe = this.stripe || window.Stripe(process.env.VUE_APP_STRIPE_KEY, {
						stripeAccount: this.agreement.account.stripeAccountId,
					});

					if (this.item.stripePaymentIntent) {
						const paymentIntent = await this.refreshPaymentIntent();
						if (!paymentIntent || paymentIntent.status !== 'requires_payment_method') return;
					}

				const family = this.resolvedStyles.body.fontFamily;
				let fonts = [];
				let stripeFamily = family;
				try {
					const response = await new CustomFontService(this.$store.state.domain.podUrl).getCustomFonts(this.agreement.accountId);
					const custom = (response.data || []).some((font) => font.fontName === family);
					if (!custom) fonts = [{ cssSrc: `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;700&display=swap` }];
					else stripeFamily = 'sans-serif';
				} catch (_) { stripeFamily = 'sans-serif'; }
				const options = {
						paymentMethodCreation: 'manual',
						fonts,
						appearance: {
							theme: this.stripeTheme,
							variables: {
								fontFamily: stripeFamily,
								fontSizeBase: this.resolvedStyles.body.fontSize + 'px',
								colorBackground: this.item.backgroundColor,
								colorText: this.stripeContrast,
								...(this.branding ? { colorPrimary: this.branding.colors.primary, colorBackground: this.branding.colors.surface, colorText: this.branding.surfaceText, borderRadius: `${this.branding.fields.radius}px` } : {}),
							},
							...(this.branding ? { rules: {
								'.Input': {
									backgroundColor: formBrandStyle(this.branding)['--brand-field-fill'],
									color: this.branding.typography.roles.answer.color,
									border: `${this.branding.fields.borderWidth}px solid ${this.branding.fields.border}`,
									borderRadius: `${this.branding.fields.radius}px`,
									padding: `${this.branding.fields.paddingY}px ${this.branding.fields.paddingX}px`,
								},
								'.Input:focus': { borderColor: this.branding.states.focusBorder },
							} } : {}),
						},
					};

					if (this.item.stripePaymentIntent) {
						options.clientSecret = this.item.stripePaymentIntent.clientSecret;
					} else {
						options.mode = 'payment';
						options.amount = 100;
						options.currency = this.agreement.currency ? this.agreement.currency.toLowerCase() : 'usd';
					}
					// A public link has no payment yet: Stripe collects the card for the amount
					// due, and the server creates and charges the payment when the visitor finishes.
					if (this.publicMode) {
						options.amount = this.amountInCents(this.paymentAmount);
						options.paymentMethodTypes = ['card'];
						options.setupFutureUsage = 'off_session';
					}

					this.elements = this.stripe.elements(options);
					this.paymentElement = this.elements.create('payment', {
						layout: { type: 'accordion', defaultCollapsed: false, visibleAccordionItemsCount: 0 },
					});
					this.paymentElement.on('change', (event) => {
						if (event.value && event.value.type) this.selectedPaymentMethodType = event.value.type;
					});
					this.paymentElement.mount('#payment-element');
					this.syncPaymentIntentAmount().catch((err) => console.error('Failed to sync payment intent amount', err));
				}
			},

			// The PaymentIntent is created at send time with a placeholder amount, and wallet
			// sheets (Google Pay / Apple Pay) display the intent's amount when elements.submit()
			// runs. Keep the intent at the real first-payment amount before the client submits.
			syncPaymentIntentAmount() {
				this.amountSync = this.amountSync.catch(() => {}).then(async () => {
					const amount = this.paymentAmount;
					if (this.publicMode) {
						if (!this.elements || !(amount > 0) || amount === this.syncedAmount) return;
						this.elements.update({ amount: this.amountInCents(amount) });
						this.syncedAmount = amount;
						return;
					}
					const intent = this.item.stripePaymentIntent;
					if (!this.elements || !intent || intent.status !== 'requires_payment_method') return;
					if (!(amount > 0) || amount === this.syncedAmount) return;

					const result = await this.httpClient.post(
						`/api/docs/agreement/paymentIntent`,
						qs.stringify({ id: this.agreement.id, amount: amount }),
						{ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
					);
					this.item.stripePaymentIntent = result.data;
					this.syncedAmount = amount;

					const { error } = await this.elements.fetchUpdates();
					if (error) throw error;
				});
				return this.amountSync;
			},

			finalize() {},

			amountInCents(amount) {
				return Math.max(1, Math.round(Number(amount) * 100));
			},

			// Public link Finish: check the card and hand back a one-time payment method for the
			// server to charge. False when the visitor needs to fix something (already explained).
			async preparePublicPayment() {
				if (!this.paymentRequired || !(this.paymentAmount > 0)) return { required: false };
				try {
					if (!this.elements || !this.stripe) return this.blockSigning('unavailable');
					await this.syncPaymentIntentAmount();
					const { error: submitError } = await this.elements.submit();
					if (submitError) return this.blockSigning(null, submitError.message);
					const { error, paymentMethod } = await this.stripe.createPaymentMethod({ elements: this.elements });
					if (error) return this.blockSigning(null, error.message);
					this.goodState();
					return { required: true, paymentMethodId: paymentMethod.id, amount: this.paymentAmount };
				} catch (err) {
					console.error('Agreement payment failed', err);
					return this.blockSigning('failed');
				}
			},

			// The card needs the visitor's bank to confirm it (e.g. 3-D Secure)
			async completePublicAction(clientSecret) {
				const { error, paymentIntent } = await this.stripe.handleNextAction({ clientSecret });
				if (error) return this.blockSigning(null, error.message);
				if (!isPaymentAuthorized(paymentIntent)) return this.blockSigning(paymentBlockedReason(paymentIntent));
				return true;
			},

			reset: function() {},

			calculateAdditionalFee: function(feeDecimal, netAmount) {
				let totalInvoice = netAmount / (1 - feeDecimal);
				return totalInvoice - netAmount;
			},

			async refreshPaymentIntent() {
				if (!this.stripe || !this.item.stripePaymentIntent) return null;
				const { paymentIntent, error } = await this.stripe.retrievePaymentIntent(this.item.stripePaymentIntent.clientSecret);
				if (error) {
					console.error('Failed to refresh payment intent', error);
					return null;
				}
				this.applyPaymentIntent(paymentIntent);
				return paymentIntent;
			},

			applyPaymentIntent(paymentIntent) {
				this.item.stripePaymentIntent.status = paymentIntent.status;
				this.item.stripePaymentIntent.amount = paymentIntent.amount;
				this.nextAction = paymentIntent.next_action || null;

				const collect = paymentIntent.status === 'requires_payment_method';
				if (!collect && this.paymentElement) {
					this.paymentElement.unmount();
					this.paymentElement = null;
					this.elements = null;
				}
				// A failed bank verification returns the intent to collection; remount the Stripe element.
				const reopen = collect && !this.showPaymentElement;
				this.showPaymentElement = collect;
				if (reopen) this.$nextTick(() => this.setupStripePayment());
			},

			refreshPendingVerification() {
				if (document.visibilityState === 'hidden' || !this.verification || this.verifying) return;
				this.refreshPaymentIntent().catch((err) => console.error('Failed to refresh payment intent', err));
			},

			async checkVerification() {
				this.verificationError = null;
				const paymentIntent = await this.refreshPaymentIntent();
				if (isPaymentAuthorized(paymentIntent)) {
					this.goodState();
					this.$store.commit('success', this.$t('agreement-payment.verified'));
				} else if (microdepositVerification(paymentIntent)) {
					this.verificationError = this.$t('agreement-payment.still-pending');
				}
			},

			async verifyMicrodeposits() {
				const amounts = this.verificationAmounts.map(parseMicrodepositAmount);
				const code = this.verificationCode.trim().toUpperCase();
				let details;
				if (this.verification.type === 'amounts') {
					if (amounts.includes(null)) {
						this.verificationError = this.$t('agreement-payment.amounts-required');
						return;
					}
					details = { amounts };
				} else {
					if (!/^SM[A-Z0-9]{4}$/.test(code)) {
						this.verificationError = this.$t('agreement-payment.code-required');
						return;
					}
					details = { descriptor_code: code };
				}

				this.verifying = true;
				this.verificationError = null;
				try {
					const { paymentIntent, error } = await this.stripe.verifyMicrodepositsForPayment(
						this.item.stripePaymentIntent.clientSecret,
						details
					);
					if (error) {
						// Stripe's message says how many attempts remain; after the last one the intent needs a new payment method.
						const refreshed = await this.refreshPaymentIntent();
						if (refreshed && refreshed.status === 'requires_payment_method') {
							this.$store.commit('error', this.$t('agreement-payment.verification-reset'));
						} else {
							this.verificationError = error.message;
						}
						return;
					}
					this.applyPaymentIntent(paymentIntent);
					if (isPaymentAuthorized(paymentIntent)) {
						this.goodState();
						this.$store.commit('success', this.$t('agreement-payment.verified'));
					}
				} catch (err) {
					console.error('Microdeposit verification failed', err);
					this.verificationError = this.$t('agreement-payment.verification-failed');
				} finally {
					this.verifying = false;
				}
			},

			// Returns true when the payment is authorized and signing may continue. Every other outcome tells
			// the client why, so a blocked submission never looks like the agreement silently failing.
			async processPayment() {
				if (!this.paymentRequired) return true;
				try {
					let paymentIntent = this.item.stripePaymentIntent;
					if (!paymentIntent || !this.stripe) return this.blockSigning('unavailable');

					if (paymentIntent.status !== 'requires_payment_method') {
						// The status may have moved on elsewhere, e.g. the bank was verified in another tab.
						paymentIntent = (await this.refreshPaymentIntent()) || paymentIntent;
						if (isPaymentAuthorized(paymentIntent)) return this.allowSigning();
						return this.blockSigning(paymentBlockedReason({ ...paymentIntent, next_action: this.nextAction }));
					}

					await this.syncPaymentIntentAmount();

					const { error: submitError } = await this.elements.submit();
					if (submitError) return this.blockSigning(null, submitError.message);

					const { error: paymentMethodError, paymentMethod } = await this.stripe.createPaymentMethod({ elements: this.elements });
					if (paymentMethodError) return this.blockSigning(null, paymentMethodError.message);

					// Normally a no-op; covers a method type the change event didn't report.
					this.selectedPaymentMethodType = paymentMethod.type;
					await this.syncPaymentIntentAmount();

					const { error, paymentIntent: confirmed } = await this.stripe.confirmPayment({
						elements: this.elements,
						redirect: 'if_required',
					});
					if (error) return this.blockSigning(null, error.message);

					this.applyPaymentIntent(confirmed);
					if (isPaymentAuthorized(confirmed)) return this.allowSigning();
					return this.blockSigning(paymentBlockedReason(confirmed));
				} catch (err) {
					console.error('Agreement payment failed', err);
					const message = err && err.response && err.response.data && err.response.data.message;
					return this.blockSigning('failed', message);
				}
			},

			allowSigning() {
				this.goodState();
				return true;
			},

			blockSigning(reason, message) {
				// Bank verification is an expected next step, not a failure.
				this.$store.commit(reason === 'verify-bank-first' ? 'warn' : 'error', message || this.$t(`agreement-payment.blocked-${reason}`));
				this.errorState();
				return false;
			},

			errorState: function() {
				document.getElementById(this.item.id).scrollIntoView({ block: 'start', behavior: 'smooth' });
				document.getElementById(this.item.id).classList.add('red-border');
			},

			goodState: function() {
				document.getElementById(this.item.id).classList.remove('red-border');
			},
		},

		computed: {
			verification() {
				const intent = this.item.stripePaymentIntent;
				return intent ? microdepositVerification({ status: intent.status, next_action: this.nextAction }) : null;
			},
			statusKey() {
				const status = this.item.stripePaymentIntent.status;
				return ['succeeded', 'processing', 'canceled'].includes(status) ? status : 'incomplete';
			},
			summaryBlockedReason() {
				const intent = this.item.stripePaymentIntent;
				return paymentBlockedReason({ status: intent.status, next_action: this.nextAction });
			},
			paymentMethodLabel() {
				const method = this.item.stripePaymentIntent.clientPaymentMethod;
				return method ? [method.type, method.label, method.last4].filter(Boolean).join(' - ') : null;
			},
			paymentAmount() {
				let amount = Number(this.tokens.get('UpFront.UnformattedTotal'));
				const cardTypes = ['apple_pay', 'google_pay', 'microsoft_pay', 'card'];

				if (cardTypes.includes(this.selectedPaymentMethodType)) {
					if (this.isPackageOfferAgreement()) {
						amount = packageOfferPaymentAmount(this.tokens, this.selectedPaymentMethodType);
					} else if (this.agreement.client.whoPaysCardFees === 'Client') {
						amount = amount + this.calculateAdditionalFee(this.agreement.account.baseCardFeeRate, amount);
					} else if (this.agreement.client.whoPaysCardFees === 'Split') {
						amount = amount + this.calculateAdditionalFee(this.agreement.account.splitCardFeeRate, amount);
					}
				}
				return amount;
			},
			publicMode() {
				return this.$store.state.publicAgreement === true;
			},
			paymentRequired() {
				if (!this.isPackageOfferAgreement()) return true;
				return Number(this.tokens.get('UpFront.UnformattedTotal')) > 0;
			},
			branding() { return agreementBranding(this.agreement); },
			stripeTheme() {
				return this.branding && chroma(this.branding.colors.background).luminance() < 0.5 ? 'night' : 'stripe';
			},
			resolvedStyles() { return agreementStyles(this.agreement); },
			stripeContrast: function() {
				let black = chroma.contrast(this.item.backgroundColor, '#000000');
				let white = chroma.contrast(this.item.backgroundColor, '#FFFFFF');
				if (black > white) {
					return '#3C3F44';
				} else {
					return '#FFFFFF';
				}
			},

			paymentStyle: function() {
				let style = `width: ${this.item.scale}%; padding: ${this.item.padding}px; margin-top: ${this.item.pt}px; margin-bottom: ${this.item.pb}px; margin-left: ${this.padding(this.item.pl)}px; margin-right: ${this.padding(this.item.pr)}px; `;
				if (this.item.backgroundColor) {
					style = style + `background-color: ${this.item.backgroundColor}; `;
				}
				if (this.item.borderColor) {
					style = style + `border-color: ${this.item.borderColor}; `;
				}
				if (this.item.borderWidth) {
					style = style + `border-style: solid; border-width: ${this.item.borderWidth}px; `;
				}
				if (this.item.borderRadius) {
					style = style + `border-radius: ${this.item.borderRadius}px; `;
				}
				return style;
			},
		},

		watch: {
			paymentAmount() {
				this.syncPaymentIntentAmount().catch((err) => console.error('Failed to sync payment intent amount', err));
			},
			paymentRequired(required) {
				if (required) {
					this.$nextTick(() => this.setupStripePayment());
				} else if (this.paymentElement) {
					this.paymentElement.unmount();
					this.paymentElement = null;
					this.elements = null;
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	.payment-verification__error {
		color: var(--v-error-base);
	}
</style>
